/**
 * Audit Trail Page
 *
 * Reports → Audit Trail
 *
 * Permissions gate: auditLog.enabled (mocked — BE to wire in parallel)
 * Mock data is used throughout; real API endpoints will be wired up in parallel
 * by the engineering team.
 */
import React, { useCallback, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '@/components/blocks/page-header';
import { ReportNoAccessCard } from '@/components/shared/reports/ReportNoAccessCard';
import { usePermissions } from '@/contexts/PermissionContext';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import { useReportFilterOptions } from '@/hooks/useReportFilterOptions';
import { useReportColumns } from '@/hooks/useReportColumns';
import { ScrollText, AlertCircle } from 'lucide-react';
import type { AuditTrailRow } from '@/utils/AuditTrail/types';
import { AUDIT_DEFAULT_COLUMNS, PAGE_SIZE } from '@/utils/AuditTrail/constants';
import { normalisePageSize } from '@/utils/reportPageSize';
import { useAuditTrailFilters } from '@/hooks/AuditTrail/useAuditTrailFilters';
import { useAuditTrailData } from '@/hooks/AuditTrail/useAuditTrailData';
import { useAuditTrailExport } from '@/hooks/AuditTrail/useAuditTrailExport';
import { DetailDrawer } from './DetailDrawer';
import { SummaryBar } from './SummaryBar';
import { ResultsTable } from './ResultsTable';
import { AuditTrailToolbar } from './AuditTrailToolbar';
import { AuditTrailFilterChips } from './AuditTrailFilterChips';
import { AuditTrailFilterSheet } from './AuditTrailFilterSheet';

const AuditTrailHeader: React.FC = () => (
  <PageHeader
    title="Audit Trail"
    subtitle="Full event history for timesheet submissions and approvals"
    size={36}
    showActions={false}
    showBreadcrumb={false}
    icon={<ScrollText />}
  />
);

export const AuditTrailPage: React.FC = () => {
  const { canViewAuditLog, canExportAuditLog } = usePermissions();
  const { currentCompany } = useCompany();
  const canView = canViewAuditLog();
  const allowExport = canExportAuditLog();

  const columns = useReportColumns(AUDIT_DEFAULT_COLUMNS);

  // Drawer state
  const [selectedRow, setSelectedRow] = useState<AuditTrailRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filterOptions = useReportFilterOptions(currentCompany?.id, { enabled: canView });
  const filters = useAuditTrailFilters();
  const { setPage } = filters;
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(normalisePageSize(size, PAGE_SIZE));
    setPage(1);
  }, [setPage]);
  const { result, summary, summaryError, rows, isLoading, loadError, accessDenied } = useAuditTrailData({
    canView,
    query: filters.query,
    page: filters.page,
    pageSize,
    companyId: currentCompany?.id,
  });
  const { isExporting, handleExport } = useAuditTrailExport({ allowExport, query: filters.query });

  // Row click
  const handleRowClick = (row: AuditTrailRow) => {
    setSelectedRow(row);
    setDrawerOpen(true);
  };

  // ── Permission gate ───────────────────────────────────────────────────────────
  if (!canView || accessDenied) {
    return (
      <div className="space-y-6">
        <AuditTrailHeader />
        <ReportNoAccessCard />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <AuditTrailHeader />

      {/* Summary Bar */}
      <SummaryBar
        total={summary?.totalTimesheetsCount ?? 0}
        submitted={summary?.submittedCount ?? 0}
        approved={summary?.approvedCount ?? 0}
        rejected={summary?.rejectedCount ?? 0}
        recalled={summary?.recalledCount ?? 0}
        drafts={summary?.draftCount ?? 0}
      />

      {/* Main card */}
      <Card>
        <CardContent className="pt-6 pb-3 px-6">
          <AuditTrailToolbar
            filters={filters}
            columns={columns}
            allowExport={allowExport}
            isExporting={isExporting}
            onExport={handleExport}
          />

          <AuditTrailFilterChips filters={filters} filterOptions={filterOptions} />

          {(loadError || summaryError) && (
            <div className="flex items-start gap-2 rounded-md border border-error-200 bg-error-50 p-3 text-sm text-error-800 dark:border-error-800 dark:bg-error-900/20 dark:text-error-300 mb-4">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{[loadError, summaryError].filter(Boolean).join(' ')}</span>
            </div>
          )}

          {/* Results Table */}
          <ResultsTable
            data={rows}
            totalCount={result?.totalCount ?? 0}
            isLoading={isLoading}
            onRowClick={handleRowClick}
            sortKey={filters.sortKey}
            sortDir={filters.sortDir}
            onSort={filters.handleSort}
            page={filters.page}
            onPageChange={filters.setPage}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            columnVisibility={columns.columnVisibility}
          />
        </CardContent>
      </Card>

      <AuditTrailFilterSheet filters={filters} filterOptions={filterOptions} />

      {/* Detail Drawer */}
      <DetailDrawer
        row={selectedRow}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  );
};
